import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { movieApi, User, tokenManager, handleApiError, isEmailNotVerifiedError } from '../services/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string; emailVerificationRequired?: boolean }>;
  register: (userData: RegisterData) => Promise<{ success: boolean; error?: string; emailVerificationRequired?: boolean; message?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

interface RegisterData {
  username: string;
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  // Check for existing session on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const token = tokenManager.getToken();
      if (token) {
        try {
          const response = await movieApi.getCurrentUser();
          setUser(response.user);
        } catch (error) {
          // Token is invalid, remove it
          tokenManager.removeToken();
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (username: string, password: string): Promise<{ success: boolean; error?: string; emailVerificationRequired?: boolean }> => {
    try {
      const response = await movieApi.login({ username, password });

      if (response.success && response.token && response.user) {
        tokenManager.setToken(response.token);
        setUser(response.user);

        if (!tokenManager.getToken()) {
          console.error('Critical: Token was not stored after login');
          return { success: false, error: 'Authentication setup failed' };
        }

        return { success: true };
      } else {
        return { success: false, error: response.error || 'Login failed' };
      }
    } catch (error) {
      if (isEmailNotVerifiedError(error)) {
        return { success: false, error: handleApiError(error), emailVerificationRequired: true };
      }
      return { success: false, error: handleApiError(error) };
    }
  };

  const register = async (userData: RegisterData): Promise<{ success: boolean; error?: string; emailVerificationRequired?: boolean; message?: string }> => {
    try {
      const response = await movieApi.register(userData);

      if (response.success) {
        if (response.email_verification_required) {
          // New accounts must verify their email before they can log in.
          return { success: true, emailVerificationRequired: true, message: response.message };
        }
        // Otherwise, automatically log in.
        return await login(userData.username, userData.password);
      } else {
        return { success: false, error: response.error || 'Registration failed' };
      }
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await movieApi.logout();
    } catch (error) {
      // Even if logout API call fails, we should still clear local state
      console.error('Logout API call failed:', error);
    } finally {
      tokenManager.removeToken();
      setUser(null);
    }
  };

  const refreshUser = async (): Promise<void> => {
    if (!tokenManager.getToken()) {
      setUser(null);
      return;
    }

    try {
      const response = await movieApi.getCurrentUser();
      setUser(response.user);
    } catch (error) {
      tokenManager.removeToken();
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;